/**
 * Transaction Sync Hook
 *
 * Custom hook for triggering Plaid transaction synchronization.
 *
 * @fileoverview React hook for transaction sync operations
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useUser } from '../Context/userContext';
import { TransactionSyncResult, TransactionResetResult, SyncStatus } from '../types/transactions';
import { apiJson } from '../lib/api';
import { toUserMessage } from '../lib/apiError';

/**
 * POST /api/plaid-transactions/sync returns this instead of a
 * TransactionSyncResult when Redis is configured — the backend hands the
 * job to its queue rather than running it inline. See
 * GET /api/plaid-transactions/sync/:jobId (SyncJobStatusResponse below).
 */
interface QueuedSyncResponse {
  status: 'queued';
  jobId: string;
}

/** Mirrors the backend's SyncJobStatusResponse (plaid-transaction-sync.controller.ts). */
interface SyncJobStatusResponse {
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'unknown';
  result?: TransactionSyncResult;
  error?: string;
}

const isQueuedResponse = (
  value: TransactionSyncResult | QueuedSyncResponse
): value is QueuedSyncResponse => (value as QueuedSyncResponse).status === 'queued' && 'jobId' in value;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// A manual sync is a handful of Plaid API calls; if it hasn't finished in
// this long something is wrong (or Plaid itself is stuck) and we stop
// polling rather than leave the UI spinning forever.
const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 2 * 60 * 1000;

/**
 * Return type for useTransactionSync hook
 */
interface UseTransactionSyncReturn {
  /** Trigger incremental sync (from cursor). Pass tokenId to sync specific bank. */
  syncIncremental: (tokenId?: string, accountIds?: string[]) => Promise<TransactionSyncResult | null>;
  /** Trigger full sync (reset cursor, fetch all). Pass tokenId to sync specific bank. */
  syncFull: (tokenId?: string, accountIds?: string[]) => Promise<TransactionSyncResult | null>;
  /** Delete all transactions for the tenant (hard reset) */
  resetTransactions: () => Promise<TransactionResetResult | null>;
  /** Current sync status */
  status: SyncStatus;
  /** Which sync type is currently running */
  syncType: 'incremental' | 'full' | 'reset' | null;
  /** Result from last sync operation */
  lastResult: TransactionSyncResult | null;
  /** Result from last reset operation */
  lastResetResult: TransactionResetResult | null;
  /** Error message if sync failed */
  error: string | null;
  /** Clear error state */
  clearError: () => void;
  /** Whether any sync is in progress */
  isSyncing: boolean;
  /** Whether a reset is in progress */
  isResetting: boolean;
}

/**
 * Hook for triggering Plaid transaction sync operations
 *
 * @example
 * const { syncIncremental, syncFull, isSyncing, lastResult } = useTransactionSync();
 *
 * // Incremental sync
 * await syncIncremental();
 *
 * // Full sync (with confirmation)
 * if (confirm('Are you sure?')) {
 *   await syncFull();
 * }
 */
export function useTransactionSync(): UseTransactionSyncReturn {
  const { session } = useAuthContext();
  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; });
  const { selectedTenant } = useUser();

  const isMountedRef = useRef(true);
  useEffect(() => {
    // Must set to true on (re)mount, not just false on cleanup: React
    // StrictMode mounts → unmounts → remounts effects in development, so a
    // cleanup-only version leaves this ref stuck at false after the very
    // first render. That silently disables the poll loop below and leaves
    // the Sync button spinning forever.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [status, setStatus] = useState<SyncStatus>('idle');
  const [syncType, setSyncType] = useState<'incremental' | 'full' | 'reset' | null>(null);
  const [lastResult, setLastResult] = useState<TransactionSyncResult | null>(null);
  const [lastResetResult, setLastResetResult] = useState<TransactionResetResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
    if (status === 'error') {
      setStatus('idle');
    }
  }, [status]);

  /**
   * Poll a queued sync job until it reaches a terminal state.
   * Returns a TransactionSyncResult either way — a timeout or an
   * unrecognized job both surface as `{ success: false, error }` so callers
   * never have to special-case the polling failure modes.
   */
  const pollSyncJob = useCallback(
    async (jobId: string, tenantId: string, token: string): Promise<TransactionSyncResult> => {
      const deadline = Date.now() + POLL_TIMEOUT_MS;

      while (Date.now() < deadline) {
        if (!isMountedRef.current) {
          // Nobody's listening anymore; stop polling but still return a
          // result in case the caller (a ref-based effect, say) wants it.
          return { success: false, added: 0, modified: 0, removed: 0, error: 'Sync cancelled (component unmounted)' };
        }

        const jobStatus = await apiJson<SyncJobStatusResponse>(
          `/api/plaid-transactions/sync/${jobId}`,
          { method: 'GET', headers: { 'x-tenant-id': tenantId } },
          token
        );

        if (jobStatus.status === 'completed') {
          return jobStatus.result ?? { success: true, added: 0, modified: 0, removed: 0 };
        }
        if (jobStatus.status === 'failed') {
          return { success: false, added: 0, modified: 0, removed: 0, error: jobStatus.error || 'Sync failed' };
        }
        if (jobStatus.status === 'unknown') {
          return {
            success: false,
            added: 0,
            modified: 0,
            removed: 0,
            error: 'Sync job not found — it may have expired. Try again.',
          };
        }

        await sleep(POLL_INTERVAL_MS);
      }

      return {
        success: false,
        added: 0,
        modified: 0,
        removed: 0,
        error: 'Timed out waiting for the sync to finish. It may still complete in the background.',
      };
    },
    []
  );

  /**
   * Execute sync API call
   * @param forceFullSync - Whether to reset cursor and fetch all data
   * @param tokenId - Optional: sync only this specific bank. If not provided, syncs all banks.
   */
  const executeSync = useCallback(
    async (
      forceFullSync: boolean,
      tokenId?: string,
      accountIds?: string[],
    ): Promise<TransactionSyncResult | null> => {
      if (!sessionRef.current?.access_token || !selectedTenant) {
        setError('Not authenticated or no tenant selected');
        setStatus('error');
        return null;
      }

      // Prevent concurrent syncs
      if (status === 'syncing') {
        setError('Sync already in progress');
        return null;
      }

      setStatus('syncing');
      setSyncType(forceFullSync ? 'full' : 'incremental');
      setError(null);

      try {
        const token = sessionRef.current!.access_token;
        const tenantId = selectedTenant.id;

        const initial = await apiJson<TransactionSyncResult | QueuedSyncResponse>(
          '/api/plaid-transactions/sync',
          {
            method: 'POST',
            headers: {
              'x-tenant-id': tenantId,
            },
            body: JSON.stringify({ forceFullSync, tokenId, accountIds }),
          },
          token
        );

        // Redis is configured: the backend queued the job instead of
        // running it inline. Poll until it finishes instead of treating
        // the 200 response itself as the result.
        const result: TransactionSyncResult = isQueuedResponse(initial)
          ? await pollSyncJob(initial.jobId, tenantId, token)
          : initial;

        if (!isMountedRef.current) {
          return result;
        }

        setLastResult(result);
        setStatus(result.success ? 'success' : 'error');

        if (!result.success && result.error) {
          setError(result.error);
        }

        return result;
      } catch (err) {
        if (isMountedRef.current) {
          const errorMessage = toUserMessage(err, "We couldn't sync your transactions. Please try again.");
          setError(errorMessage);
          setStatus('error');
          setLastResult(null);
        }
        return null;
      } finally {
        if (isMountedRef.current) {
          setSyncType(null);
        }
      }
    },
    [selectedTenant, status, pollSyncJob]
  );

  /**
   * Trigger incremental sync (from cursor position)
   * @param tokenId - Optional: sync only this specific bank
   */
  const syncIncremental = useCallback(async (tokenId?: string, accountIds?: string[]): Promise<TransactionSyncResult | null> => {
    return executeSync(false, tokenId, accountIds);
  }, [executeSync]);

  /**
   * Trigger full sync (reset cursor, fetch all data)
   * @param tokenId - Optional: sync only this specific bank
   */
  const syncFull = useCallback(async (tokenId?: string, accountIds?: string[]): Promise<TransactionSyncResult | null> => {
    return executeSync(true, tokenId, accountIds);
  }, [executeSync]);

  /**
   * Delete all transactions for the tenant
   *
   * CRITICAL: This is a destructive operation that permanently deletes
   * all transactions for the authenticated tenant. The backend ensures
   * tenant isolation - only the authenticated tenant's data is deleted.
   *
   * The confirmation flow should be handled by the UI component before
   * calling this function.
   */
  const resetTransactions = useCallback(async (): Promise<TransactionResetResult | null> => {
    if (!sessionRef.current?.access_token || !selectedTenant) {
      setError('Not authenticated or no tenant selected');
      setStatus('error');
      return null;
    }

    // Prevent concurrent operations
    if (status === 'syncing') {
      setError('Operation already in progress');
      return null;
    }

    setStatus('syncing');
    setSyncType('reset');
    setError(null);

    try {
      const result: TransactionResetResult = await apiJson<TransactionResetResult>(
        '/api/plaid-transactions',
        {
          method: 'DELETE',
          headers: {
            'x-tenant-id': selectedTenant.id,
          },
        },
        sessionRef.current!.access_token
      );

      setLastResetResult(result);
      setStatus(result.resetComplete ? 'success' : 'error');

      if (!result.resetComplete) {
        setError('Reset operation did not complete successfully');
      }

      return result;
    } catch (err) {
      const errorMessage = toUserMessage(err, "We couldn't reset the sync. Please try again.");
      setError(errorMessage);
      setStatus('error');
      setLastResetResult(null);
      return null;
    } finally {
      setSyncType(null);
    }
  }, [selectedTenant, status]);

  return {
    syncIncremental,
    syncFull,
    resetTransactions,
    status,
    syncType,
    lastResult,
    lastResetResult,
    error,
    clearError,
    isSyncing: status === 'syncing' && syncType !== 'reset',
    isResetting: status === 'syncing' && syncType === 'reset',
  };
}

export default useTransactionSync;
